'use client';

import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useVideoElement } from '@/lib/hooks/useVideoElement';
import styles from './KagemaneAutoplayVideo.module.css';

/** The selected reference starts muted; native controls let the user enable sound. */
export function KagemaneAutoplayVideo({ src, poster, label, active = true, manualPlayback = false, className = '' }: {
  src: string; poster?: string; label: string; active?: boolean; manualPlayback?: boolean; className?: string;
}) {
  const { video, videoRef, autoPlayAllowed, isPlaybackAllowed } = useVideoElement({ active });
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [failed, setFailed] = useState(false);
  const motionAllowed = !reducedMotion || manualPlayback;
  const shouldPlay = visible && pageVisible && active && autoPlayAllowed && motionAllowed && !failed;

  useEffect(() => {
    const media = video.current;
    if (!media) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.1 });
    observer.observe(media);
    return () => observer.disconnect();
  }, [video]);
  useEffect(() => {
    const change = () => setPageVisible(document.visibilityState === 'visible');
    change();
    document.addEventListener('visibilitychange', change);
    return () => document.removeEventListener('visibilitychange', change);
  }, []);
  useEffect(() => { setFailed(false); }, [src]);
  useEffect(() => {
    const media = video.current;
    if (!media || !shouldPlay) { media?.pause(); return; }
    let cancelled = false;
    void (async () => {
      try {
        await media.play();
        if (cancelled || !isPlaybackAllowed()) media.pause();
      }
      catch { if (!cancelled) media.pause(); }
    })();
    return () => { cancelled = true; media.pause(); };
  }, [shouldPlay, src, video, isPlaybackAllowed]);

  return <video ref={videoRef} src={visible && active && pageVisible && !failed && motionAllowed ? src : undefined} poster={poster} aria-label={label}
    muted controls loop playsInline preload="metadata" onError={() => setFailed(true)}
    className={`${styles.video} ${className}`} />;
}
